/**
 * iOS Safari WebAudio unlock — must run synchronously inside a user gesture.
 *
 * Without this, the ElevenLabs SDK creates its AudioContext + autoplay <audio>
 * element well after the click (post fetch + getUserMedia + AudioWorklet load),
 * by which point iOS has closed the gesture window. The mic captures input but
 * the agent's reply audio never plays and AudioContext.resume() stays pending.
 *
 * The fix: open an AudioContext, resume it, and play a 1-frame silent buffer
 * — all in the same synchronous tick as the click. iOS treats that as enough
 * activation to lift the autoplay restriction for the rest of the page life.
 */

let unlocked = false;
let primedAudioEl: HTMLAudioElement | null = null;

export function unlockIOSAudio(): void {
  if (typeof window === 'undefined' || unlocked) return;
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      // resume() must be kicked off in-gesture; the promise can settle later.
      ctx.resume().catch(() => {});
      const buffer = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      src.start(0);
      // Close shortly after — we only needed it for the unlock side effect.
      setTimeout(() => ctx.close().catch(() => {}), 250);
    }
    // Also prime an <audio> element. The ElevenLabs SDK creates its own
    // <audio autoplay> later for output routing; iOS will only allow it to
    // play once a prior <audio>.play() has succeeded in-gesture.
    if (!primedAudioEl) {
      primedAudioEl = new Audio();
      primedAudioEl.muted = true;
      // playsInline is on HTMLVideoElement in lib.dom; for HTMLAudioElement
      // the attribute still works on iOS Safari, set via setAttribute.
      primedAudioEl.setAttribute('playsinline', 'true');
      // Tiny silent WAV (44-byte header, 1 sample, 8000 Hz, mono).
      primedAudioEl.src =
        'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
    }
    primedAudioEl.play().catch(() => {});
    unlocked = true;
  } catch {
    /* swallow — we're best-effort here */
  }
}
